import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { Router, ActivatedRoute } from '@angular/router';
import { MatPaginator, MatSort, MatTableDataSource, PageEvent } from '@angular/material';
import { Subject } from 'rxjs/Subject';
import { ModuleConfiguration } from '../submission.module.config';
import { JobModel } from '../models/JobModel';
import * as fromStore from '../store/reducers';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { BaseAddressInterceptor } from '../../core/interceptors/BaseAddressInterceptor';
import { ReprocessJobSubmissionAction } from '../store/actions/ReprocessJobSubmissionAction';
import { REPOROCESS_JOB_SUBMISSION_SUCCESS } from '../store/actions/ReprocessJobSubmissionSuccessAction';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { AddMappingModel } from '../models/AddMappingModel';
import { JobWorkSpacePageViewModel } from '../models/JobWorkSpacePageViewModel';
import { FilterPageViewModel } from '../models/FilterPageViewModel';

@Component({
  selector: 'brd-job-workspace',
  templateUrl: './job-workspace.component.html',
  styleUrls: ['./job-workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobWorkspaceComponent implements OnInit, OnDestroy {
  viewErrorMessageCodesPermission = 'SubmissionModule.MessageCodes:View';
  displayedColumns = ['source', 'type', 'message'];
  displayedEntityErrorColumns = this.canViewErrorCodes() ? ['source', 'type', 'number', 'message'] : ['source', 'type', 'message'];
  identifierFields = [];
  job$: Observable<JobModel>;
  key: string;
  dataSources = {};
  isJobReprocessing = false;
  canReprocess = false;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  viewXmlJobReportsPermission = 'SubmissionModule.XmlJobReports:View';
  jobWorkSpacePageViewModel: JobWorkSpacePageViewModel;
  job: JobModel;
  errorIndex = 0;
  warningIndex = 0;
  loading$: Observable<boolean>;

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
              private router: Router,
    private route: ActivatedRoute,
    private currentUserService: CurrentUserService,
    private baseAddressInterceptor: BaseAddressInterceptor,
    private ChangeDetectorRef: ChangeDetectorRef,
    private actions$: Actions) { }

  ngOnDestroy() {
    this.store.dispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, JobModel));
  }

  ngOnInit() {
    this.job$ = this.store.select(fromStore.getJobSelection);
    this.job$
    .subscribe((v: JobModel) => {
      if (v != null){
        this.job = v;
      }
    });
    this.loading$ = this.store.select(fromStore.getJobListLoading);

    this.route.params.take(1)
      .subscribe(p => {
        this.key = p['key'];
        this.setPageViewModel();
        this.store.dispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, JobModel, { model: this.jobWorkSpacePageViewModel }));
      });

    this.actions$.ofType(REPOROCESS_JOB_SUBMISSION_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.isJobReprocessing = false;
        this.canReprocess = false;
        this.alerts.addSuccess("Successfully Initialized New Job, Go back to Submission tab to see details.");
      });

    this.actions$.ofType(dashboardActions.addSuccessType<AddMappingModel>(ModuleConfiguration.moduleName, AddMappingModel))
      .takeUntil(this.destroyed$)
      .subscribe((a: dashboardActions.AddSuccessAction<AddMappingModel>) => {
        this.canReprocess = true;
        this.ChangeDetectorRef.markForCheck();
      });
  }

  back() {
    this.router.navigateByUrl( '/submission' );
  }

  getDataSource(key, array, matSort: MatSort, paginator: MatPaginator) {
    if (!this.dataSources.hasOwnProperty(key)) {
      this.setPageViewModel();
      var dataSource = new MatTableDataSource(array);
      dataSource.paginator = paginator;
      dataSource.sort = matSort;
      this.dataSources[key] = dataSource;
    }
    return this.dataSources[key];
  }

  getEntityErrors(key, array, matSort: MatSort, paginator: MatPaginator ) {
    if (!this.dataSources.hasOwnProperty(key)) {
      let index: number = 1;
      if (array != null){
        array.forEach(element => {
          for(var key in element.identifierFieldsDictionary) { 
            if(key != '$type') {
              element[key] = element.identifierFieldsDictionary[key];
              if(this.displayedEntityErrorColumns.indexOf(key) == -1){
                this.displayedEntityErrorColumns.splice(index, 0, key);
                this.identifierFields.push(key);
                index++;
              }
            }
          }
        });
        var dataSource = new MatTableDataSource(array);
        dataSource.paginator = paginator;
        dataSource.sort = matSort;
        this.dataSources[key] = dataSource;
      }
    }
    return this.dataSources[key];
  }

  refreshDataSource( key, paginator: MatPaginator ){
    if (this.dataSources.hasOwnProperty(key)) {
      this.dataSources[key].paginator = paginator;
    }
  }

  downloadExcelFile(): void {
    //TODO: Update to get Temporary token just for file download
    var url = '/api/SubmissionModule/Job/ExportToExcel/' + this.key + '/?XAuthToken=' + this.currentUserService.userContext.authToken;
    window.open(this.baseAddressInterceptor.getApiUrl(url));
  }

  downloadPdfFile(): void {
    //TODO: Update to get Temporary token just for file download
    var url = '/api/SubmissionModule/Job/ExportToPDF/' + this.key + '/?XAuthToken=' + this.currentUserService.userContext.authToken;
    window.open(this.baseAddressInterceptor.getApiUrl(url));
  }

  downloadXmlFile(): void {
    //TODO: Update to get Temporary token just for file download
    var url = '/api/SubmissionModule/Job/ExportToXml/' + this.key + '/?XAuthToken=' + this.currentUserService.userContext.authToken;
    window.open(this.baseAddressInterceptor.getApiUrl(url));
  }

  reprocessSubmission(): void {
    this.store.dispatch(new ReprocessJobSubmissionAction({ jobKey: this.key }));
    this.isJobReprocessing = true;
  }

  canViewErrorCodes() {
    return this.currentUserService.hasPermission(this.viewErrorMessageCodesPermission);
  }

  capitalizeFirstLetter(string){
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  getDataToDisplay (type: string, array: any, paginator: MatPaginator) {
    if (paginator.pageIndex != null && paginator.pageSize != null){      
      switch (type) {
        case "error":{
          paginator.length = this.job.errorEntityCount;
          paginator.pageIndex = this.errorIndex;
          return array;
  
        }
        case "warning":{
          paginator.length = this.job.warningEntityCount;
          paginator.pageIndex = this.warningIndex;
          return array;
        }
        default:{
          return;
        }
      }
    }
  }

  setPageViewModel(type: string = null, paginator: MatPaginator = null){
    if (this.jobWorkSpacePageViewModel == null){
      this.jobWorkSpacePageViewModel = new JobWorkSpacePageViewModel();
      this.jobWorkSpacePageViewModel.errorPageView = new FilterPageViewModel();
      this.jobWorkSpacePageViewModel.warningPageView = new FilterPageViewModel();
    }
    this.jobWorkSpacePageViewModel.key = this.key;
    switch (type) {
      case "error":{
        this.errorIndex = paginator.pageIndex;
        this.jobWorkSpacePageViewModel.errorPageView.skip = paginator.pageIndex * paginator.pageSize;
        this.jobWorkSpacePageViewModel.errorPageView.take =  paginator.pageSize;
        break;
      }
      case "warning":{
        this.warningIndex = paginator.pageIndex;
        this.jobWorkSpacePageViewModel.warningPageView.skip = paginator.pageIndex * paginator.pageSize;
        this.jobWorkSpacePageViewModel.warningPageView.take =  paginator.pageSize;  
        break; 
      }
      default:{
        this.jobWorkSpacePageViewModel.errorPageView.skip = 0;
        this.jobWorkSpacePageViewModel.errorPageView.take =  20;
        this.jobWorkSpacePageViewModel.warningPageView.skip = 0;
        this.jobWorkSpacePageViewModel.warningPageView.take =  20;
        break;
      }
    }
  }

  pageEvents (event: PageEvent, type: string, paginator: MatPaginator ){
    this.setPageViewModel(type, paginator);
    this.store.dispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, JobModel, { model: this.jobWorkSpacePageViewModel }));
  }
}



// WEBPACK FOOTER //
// ./src/app/submission/components/job-workspace.component.ts